import { Lock, LockOpen, Users, Bell, PencilSimple, Gear, ChartBar, Clock, NotePencil, ChartLineUp, MagnifyingGlass, ArrowsClockwise } from 'phosphor-react';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { monthLabel, monthKey, buildMonthOptions } from '../utils/dates';
import { selectMonth, toggleLock } from '../redux/slices/hoursSlice';
import { hrDashboardService } from '../services/hrDashboardService';
import { useToast } from '../components/Toast';
import { HoursState } from '../types';

type TabId = 'overview' | 'edit' | 'settings';

const AdminPage = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { selectedMonth, months } = useAppSelector((state) => state.hours);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Overview/Reminders state
  const [isSavingLock, setIsSavingLock] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [allEntries, setAllEntries] = useState<Array<{ userId: number; userName: string; hours: number; notes: string; updatedAt: string }>>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  // Edit Entries state
  const [candidates, setCandidates] = useState<any[] | null>(null);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);
  const [selectedEditUserId, setSelectedEditUserId] = useState<number | null>(null);
  const [editingState, setEditingState] = useState<HoursState | null>(null);
  const [editingHours, setEditingHours] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [isLoadingEditUser, setIsLoadingEditUser] = useState(false);
  const [isSavingEditUser, setIsSavingEditUser] = useState(false);

  // Settings state
  const [adminUsers, setAdminUsers] = useState<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>([]);
  const [fillerUsers, setFillerUsers] = useState<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>([]);
  const [slackMappings, setSlackMappings] = useState<Record<number, string>>({});
  const [slackUserDetails, setSlackUserDetails] = useState<Record<string, { id: string; name: string; displayName: string; email: string | null; avatar: string | null }>>({});
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  // User search state
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [fillerSearchQuery, setFillerSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>([]);
  const [fillerSearchResults, setFillerSearchResults] = useState<Array<{ userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }>>([]);
  const [isSearchingAdmins, setIsSearchingAdmins] = useState(false);
  const [isSearchingFillers, setIsSearchingFillers] = useState(false);

  // Slack matching modal state
  const [slackModal, setSlackModal] = useState<{
    user: { userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null } | null;
    suggestedMatches: Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>;
    selectedSlackId: string | null;
    searchQuery: string;
    searchResults: Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }>;
    isSearching: boolean;
  } | null>(null);
  const slackSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce refs for search
  const adminSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fillerSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (adminSearchTimeoutRef.current) {
      clearTimeout(adminSearchTimeoutRef.current);
    }
    if (adminSearchQuery.length < 2) {
      setAdminSearchResults([]);
      return;
    }
    setIsSearchingAdmins(true);
    adminSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await hrDashboardService.searchUsers(adminSearchQuery);
        setAdminSearchResults(results.filter(u => !adminUsers.some(au => au.userId === u.userId)));
      } catch {
        // Silently fail search
      } finally {
        setIsSearchingAdmins(false);
      }
    }, 300);
    return () => {
      if (adminSearchTimeoutRef.current) {
        clearTimeout(adminSearchTimeoutRef.current);
      }
    };
  }, [adminSearchQuery, adminUsers]);

  useEffect(() => {
    if (fillerSearchTimeoutRef.current) {
      clearTimeout(fillerSearchTimeoutRef.current);
    }
    if (fillerSearchQuery.length < 2) {
      setFillerSearchResults([]);
      return;
    }
    setIsSearchingFillers(true);
    fillerSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await hrDashboardService.searchUsers(fillerSearchQuery);
        setFillerSearchResults(results.filter(u => !fillerUsers.some(fu => fu.userId === u.userId)));
      } catch {
        // Silently fail search
      } finally {
        setIsSearchingFillers(false);
      }
    }, 300);
    return () => {
      if (fillerSearchTimeoutRef.current) {
        clearTimeout(fillerSearchTimeoutRef.current);
      }
    };
  }, [fillerSearchQuery, fillerUsers]);

  const [confirmModal, setConfirmModal] = useState<{ title: string; onConfirm: () => void } | null>(null);

  const monthOptions = buildMonthOptions(18);
  const activeMonth = selectedMonth || monthKey(new Date());
  const monthState = months[activeMonth] ?? { locked: false, entries: {} };
  // Use allEntries from API instead of personal Redux state
  const entries = allEntries;
  const totalHours = entries.reduce((sum, entry) => sum + (Number(entry.hours) || 0), 0);
  const totalUsers = entries.length;
  const avgHours = totalUsers > 0 ? totalHours / totalUsers : 0;
  const notesFilled = entries.filter((entry) => entry.notes.trim().length > 0).length;

  useEffect(() => {
    let isActive = true;

    const loadCandidates = async () => {
      try {
        const list = await hrDashboardService.getReminderCandidates(activeMonth);
        if (!isActive) {
          return;
        }

        setCandidates(list);
        setSelectedRecipientIds(list.filter((user: any) => !user.hasEntry).map((user: any) => user.id as number));
      } catch (error) {
        if (isActive) {
          showToast(error instanceof Error ? error.message : 'Failed to load recipients', 'error');
        }
      }
    };

    loadCandidates();

    return () => {
      isActive = false;
    };
  }, [activeMonth]);

  // Load all entries for the month
  useEffect(() => {
    let isActive = true;

    const loadEntries = async () => {
      setIsLoadingEntries(true);
      try {
        const entries = await hrDashboardService.getAllEntriesForMonth(activeMonth);
        if (isActive) {
          setAllEntries(entries);
        }
      } catch {
        // Silently fail
      } finally {
        if (isActive) {
          setIsLoadingEntries(false);
        }
      }
    };

    loadEntries();

    return () => {
      isActive = false;
    };
  }, [activeMonth]);

  useEffect(() => {
    let isActive = true;

    const loadSelectedUserState = async () => {
      if (!selectedEditUserId) {
        setEditingState(null);
        setEditingHours('');
        setEditingNotes('');
        return;
      }

      setIsLoadingEditUser(true);

      try {
        const state = await hrDashboardService.getUserHoursState(selectedEditUserId);
        if (!isActive) {
          return;
        }

        const monthStateForUser = state.months[activeMonth] ?? { locked: false, entries: {} };
        const entry = monthStateForUser.entries[String(selectedEditUserId)] ?? {
          month: activeMonth,
          userId: selectedEditUserId,
          userName: candidates?.find((candidate) => candidate.id === selectedEditUserId)?.name ?? '',
          hours: 0,
          notes: '',
          updatedAt: new Date().toISOString(),
        };

        setEditingState(state);
        setEditingHours(String(entry.hours ?? 0));
        setEditingNotes(entry.notes ?? '');
      } catch (error) {
        if (isActive) {
          showToast(error instanceof Error ? error.message : 'Failed to load user entry', 'error');
        }
      } finally {
        if (isActive) {
          setIsLoadingEditUser(false);
        }
      }
    };

    loadSelectedUserState();

    return () => {
      isActive = false;
    };
  }, [activeMonth, candidates, selectedEditUserId]);

  const selectedEditCandidate = candidates?.find((candidate) => candidate.id === selectedEditUserId) ?? null;
  const selectedEditMonthState = editingState?.months[activeMonth] ?? { locked: false, entries: {} };
  const selectedEditEntry =
    selectedEditUserId !== null
      ? selectedEditMonthState.entries[String(selectedEditUserId)] ?? {
          month: activeMonth,
          userId: selectedEditUserId,
          userName: selectedEditCandidate?.name ?? '',
          hours: 0,
          notes: '',
          updatedAt: new Date().toISOString(),
        }
      : null;

  const handleSaveEditedEntry = async () => {
    if (selectedEditUserId === null) {
      showToast('Select a user first', 'error');
      return;
    }

    setIsSavingEditUser(true);

    try {
      const nextState: HoursState = editingState
        ? { ...editingState, selectedMonth: editingState.selectedMonth || activeMonth, months: { ...editingState.months } }
        : { selectedMonth: activeMonth, months: {} };
      const nextMonthState = nextState.months[activeMonth] ?? { locked: false, entries: {} };
      const entryKey = String(selectedEditUserId);
      const nextEntry = {
        month: activeMonth,
        userId: selectedEditUserId,
        userName: selectedEditCandidate?.name ?? selectedEditEntry?.userName ?? '',
        hours: Number(editingHours) || 0,
        notes: editingNotes,
        updatedAt: new Date().toISOString(),
      };

      nextState.months[activeMonth] = {
        ...nextMonthState,
        entries: {
          ...nextMonthState.entries,
          [entryKey]: nextEntry,
        },
      };

      await hrDashboardService.saveUserHoursState(selectedEditUserId, nextState);
      setEditingState(nextState);
      setCandidates((current) =>
        current?.map((candidate) =>
          candidate.id === selectedEditUserId ? { ...candidate, hasEntry: true } : candidate,
        ) ?? current,
      );
      setSelectedRecipientIds((current) => (current.includes(selectedEditUserId) ? current.filter((id) => id !== selectedEditUserId) : current));
      showToast(`Saved entry for ${selectedEditCandidate?.name ?? selectedEditUserId}`);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save user entry', 'error');
    } finally {
      setIsSavingEditUser(false);
    }
  };

  const loadSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const [settings, mappings] = await Promise.all([
        hrDashboardService.getSettings(),
        hrDashboardService.getSlackMappings().catch(() => ({} as Record<number, string>)),
      ]);
      // Fetch user details for the IDs
      const [adminUserDetails, fillerUserDetails] = await Promise.all([
        hrDashboardService.getUsersByIds(settings.admins),
        hrDashboardService.getUsersByIds(settings.fillers),
      ]);
      setAdminUsers(adminUserDetails);
      setFillerUsers(fillerUserDetails);
      setSlackMappings(mappings);

      // Fetch real Slack user details for existing mappings
      const uniqueIds = [...new Set(Object.values(mappings))];
      if (uniqueIds.length > 0) {
        const details: Record<string, { id: string; name: string; displayName: string; email: string | null; avatar: string | null }> = {};
        try {
          const slackUsers = await hrDashboardService.getSlackUsersByIds(uniqueIds);
          for (const slackUser of slackUsers) {
            details[slackUser.id] = slackUser;
          }
        } catch {
          // Ignore errors; fall back to placeholders below
        }
        // Ensure every mapped ID has at least a placeholder
        for (const slackId of uniqueIds) {
          if (!details[slackId]) {
            details[slackId] = { id: slackId, name: '', displayName: 'Matched Slack user', email: null, avatar: null };
          }
        }
        setSlackUserDetails(details);
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to load settings', 'error');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Load settings when settings tab becomes active (and not already loaded)
  useEffect(() => {
    if (activeTab === 'settings' && adminUsers.length === 0 && fillerUsers.length === 0 && !isLoadingSettings) {
      loadSettings();
    }
  }, [activeTab]);

  const handleAdminSearchChange = (value: string) => {
    setAdminSearchQuery(value);
  };

  const handleFillerSearchChange = (value: string) => {
    setFillerSearchQuery(value);
  };

  const persistMembers = async (
    admins: Array<{ userId: number }>,
    fillers: Array<{ userId: number }>,
  ) => {
    setIsSavingSettings(true);
    try {
      await hrDashboardService.updateSettings({
        admins: admins.map(u => u.userId),
        fillers: fillers.map(u => u.userId),
      });
      showToast('Settings saved');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save settings', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const addAdminUser = (user: { userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }) => {
    setAdminSearchQuery('');
    setAdminSearchResults([]);
    if (adminUsers.some(u => u.userId === user.userId)) {
      return;
    }
    const nextAdmins = [...adminUsers, user];
    setAdminUsers(nextAdmins);
    persistMembers(nextAdmins, fillerUsers);
  };

  const removeAdminUser = (userId: number) => {
    const nextAdmins = adminUsers.filter(u => u.userId !== userId);
    setAdminUsers(nextAdmins);
    persistMembers(nextAdmins, fillerUsers);
  };

  const addFillerUser = async (user: { userId: number; firstName: string | null; lastName: string | null; primaryEmail: string | null }) => {
    if (fillerUsers.some(u => u.userId === user.userId)) {
      setFillerSearchQuery('');
      setFillerSearchResults([]);
      return;
    }

    setFillerSearchQuery('');
    setFillerSearchResults([]);

    // Check if user already has a Slack mapping
    if (slackMappings[user.userId]) {
      const nextFillers = [...fillerUsers, user];
      setFillerUsers(nextFillers);
      persistMembers(adminUsers, nextFillers);
      return;
    }

    // Search for matching Slack users
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || '';
    const searchTerms = [user.primaryEmail, displayName, user.firstName, user.lastName].filter(Boolean) as string[];

    let suggestedMatches: Array<{ id: string; name: string; displayName: string; email: string | null; avatar: string | null }> = [];

    for (const term of searchTerms) {
      if (term.length < 2) continue;
      try {
        const results = await hrDashboardService.searchSlackUsers(term);
        suggestedMatches = [...suggestedMatches, ...results];
      } catch {
        // Silently continue
      }
    }

    // Deduplicate by ID
    const uniqueMatches = suggestedMatches.filter((match, index, self) =>
      index === self.findIndex(m => m.id === match.id)
    ).slice(0, 5);

    // Show Slack matching modal
    setSlackModal({
      user,
      suggestedMatches: uniqueMatches,
      selectedSlackId: uniqueMatches.length > 0 ? uniqueMatches[0].id : null,
      searchQuery: '',
      searchResults: [],
      isSearching: false,
    });
  };

  const confirmSlackMatch = () => {
    if (!slackModal?.user) return;

    const user = slackModal.user;
    const slackId = slackModal.selectedSlackId;

    // Add user to filler list
    const nextFillers = [...fillerUsers, user];
    setFillerUsers(nextFillers);
    persistMembers(adminUsers, nextFillers);

    // Save Slack mapping if selected
    if (slackId && slackModal) {
      const newMappings = { ...slackMappings, [user.userId]: slackId };
      setSlackMappings(newMappings);

      // Store Slack user details
      const selectedMatch = slackModal.suggestedMatches.find(m => m.id === slackId)
        || slackModal.searchResults.find(m => m.id === slackId);
      if (selectedMatch) {
        setSlackUserDetails(prev => ({ ...prev, [slackId]: selectedMatch }));
      }

      // Auto-save mappings to backend
      hrDashboardService.updateSlackMappings(newMappings).catch(console.error);
    }

    setSlackModal(null);
  };

  const skipSlackMatch = () => {
    if (!slackModal?.user) return;
    // Add user without Slack mapping
    const nextFillers = [...fillerUsers, slackModal.user];
    setFillerUsers(nextFillers);
    persistMembers(adminUsers, nextFillers);
    setSlackModal(null);
  };

  const handleSlackSearchChange = (value: string) => {
    if (!slackModal) return;
    setSlackModal({ ...slackModal, searchQuery: value, isSearching: value.length >= 2 });

    if (slackSearchTimeoutRef.current) {
      clearTimeout(slackSearchTimeoutRef.current);
    }

    if (value.length < 2) {
      setSlackModal(prev => prev ? { ...prev, searchResults: [] } : null);
      return;
    }

    slackSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await hrDashboardService.searchSlackUsers(value);
        setSlackModal(prev => prev ? { ...prev, searchResults: results, isSearching: false } : null);
      } catch {
        setSlackModal(prev => prev ? { ...prev, isSearching: false } : null);
      }
    }, 300);
  };

  const removeFillerUser = (userId: number) => {
    const nextFillers = fillerUsers.filter(u => u.userId !== userId);
    setFillerUsers(nextFillers);
    persistMembers(adminUsers, nextFillers);
  };

  const handleClearEditedEntry = async () => {
    if (selectedEditUserId === null) {
      showToast('Select a user first', 'error');
      return;
    }

    setIsSavingEditUser(true);

    try {
      const nextState: HoursState = editingState
        ? { ...editingState, selectedMonth: editingState.selectedMonth || activeMonth, months: { ...editingState.months } }
        : { selectedMonth: activeMonth, months: {} };
      const nextMonthState = nextState.months[activeMonth] ?? { locked: false, entries: {} };
      const nextEntries = { ...nextMonthState.entries };
      delete nextEntries[String(selectedEditUserId)];

      nextState.months[activeMonth] = {
        ...nextMonthState,
        entries: nextEntries,
      };

      await hrDashboardService.saveUserHoursState(selectedEditUserId, nextState);
      setEditingState(nextState);
      setEditingHours('');
      setEditingNotes('');
      setCandidates((current) =>
        current?.map((candidate) =>
          candidate.id === selectedEditUserId ? { ...candidate, hasEntry: false } : candidate,
        ) ?? current,
      );
      setSelectedRecipientIds((current) => (current.includes(selectedEditUserId) ? current : [...current, selectedEditUserId]));
      showToast(`Cleared entry for ${selectedEditCandidate?.name ?? selectedEditUserId}`);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to clear user entry', 'error');
    } finally {
      setIsSavingEditUser(false);
    }
  };

  const handleToggleLock = async () => {
    const nextMonths = {
      ...months,
      [activeMonth]: {
        ...monthState,
        locked: !monthState.locked,
      },
    };

    dispatch(toggleLock(activeMonth));
    setIsSavingLock(true);

    try {
      await hrDashboardService.saveHoursState({
        selectedMonth,
        months: nextMonths,
      });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save month lock', 'error');
    } finally {
      setIsSavingLock(false);
    }
  };

  const tabs: { id: TabId; label: string; description: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', description: 'Stats, reminders & team hours', icon: <ChartBar size={20} weight="fill" /> },
    { id: 'edit', label: 'Edit Entries', description: 'Adjust a user\u2019s hours', icon: <PencilSimple size={20} weight="fill" /> },
    { id: 'settings', label: 'Settings', description: 'Admins & fillers', icon: <Gear size={20} weight="fill" /> },
  ];

  const stats = [
    { label: 'Total hours', value: totalHours.toFixed(2), hint: 'All submitted hours this month', icon: <Clock size={20} weight="fill" />, accent: 'text-brand-200', ring: 'from-brand-400/25 to-brand-600/10' },
    { label: 'Users with entries', value: String(totalUsers), hint: 'Team members who saved a total', icon: <Users size={20} weight="fill" />, accent: 'text-white', ring: 'from-sky-400/25 to-sky-600/10' },
    { label: 'Average per user', value: avgHours.toFixed(2), hint: 'Average submitted hours', icon: <ChartLineUp size={20} weight="fill" />, accent: 'text-white', ring: 'from-violet-400/25 to-violet-600/10' },
    { label: 'Notes added', value: String(notesFilled), hint: 'Entries with supporting notes', icon: <NotePencil size={20} weight="fill" />, accent: 'text-white', ring: 'from-amber-400/25 to-amber-600/10' },
  ];

  return (
    <div className="space-y-6">
      {/* Header with month selector */}
      <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.12] to-white/[0.04] p-6 text-white shadow-glow backdrop-blur-2xl sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.25em] text-brand-200/90">
              <Gear size={14} weight="fill" /> Administration
            </div>
            <h1 className="mt-3 text-3xl font-semibold sm:text-4xl">{monthLabel(activeMonth)}</h1>
            <p className="mt-3 max-w-2xl text-sm text-white/70">
              Manage HR dashboard hours, send reminders, and configure settings.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <label className="text-xs uppercase tracking-[0.2em] text-white/45">Month</label>
            <select
              value={activeMonth}
              onChange={(event) => dispatch(selectMonth(event.target.value))}
              className="min-w-44 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
            >
              {monthOptions.map((month) => (
                <option key={month} value={month} className="bg-slate-900">
                  {monthLabel(month)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <nav className="no-scrollbar -mt-2 flex items-center gap-1 overflow-x-auto border-b border-white/10">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={`group relative inline-flex items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                isActive ? 'text-white' : 'text-white/55 hover:text-white/90'
              }`}
            >
              <span className={isActive ? 'text-brand-300' : 'text-white/45 group-hover:text-white/70'}>{tab.icon}</span>
              {tab.label}
              <span
                className={`absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand-400 transition-all duration-200 ${
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'
                }`}
              />
            </button>
          );
        })}
      </nav>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Stats Cards */}
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="group rounded-[1.75rem] border border-white/10 bg-white/[0.07] p-5 text-white shadow-glow backdrop-blur-2xl transition hover:border-brand-400/30 hover:bg-white/[0.11]"
              >
                <div className="flex items-start justify-between">
                  <div className="text-xs uppercase tracking-[0.25em] text-white/45">{stat.label}</div>
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${stat.ring} text-white ring-1 ring-white/10`}>
                    {stat.icon}
                  </div>
                </div>
                <div className={`mt-3 text-3xl font-semibold ${stat.accent}`}>{stat.value}</div>
                <div className="mt-2 text-sm text-white/60">{stat.hint}</div>
              </div>
            ))}
          </section>

          {/* Actions Section */}
          <section className="rounded-[2rem] border border-white/10 bg-white/10 p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-sm uppercase tracking-[0.3em] text-brand-200/90">Actions</div>
                <h2 className="mt-2 text-2xl font-semibold text-white">Reminders & Lock</h2>
                <p className="mt-2 text-sm text-white/65">
                  Send Slack reminders to users or lock/unlock the month for editing.
                </p>
              </div>
              <div className="text-sm text-white/45">Month: {monthLabel(activeMonth)}</div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setConfirmModal({
                    title: `Send reminder to all users missing entries for ${monthLabel(activeMonth)}?`,
                    onConfirm: async () => {
                      setConfirmModal(null);
                      setIsSendingReminders(true);
                      try {
                        const res = await hrDashboardService.sendSlackReminders(activeMonth);
                        const skippedNote =
                          typeof res?.skipped === 'number' && res.skipped > 0 ? `, skipped ${res.skipped}` : '';
                        showToast(`Reminders sent: ${res?.sent ?? 0} / ${res?.requested ?? 0}${skippedNote}`);
                      } catch (err) {
                        showToast(err instanceof Error ? err.message : 'Failed to send reminders', 'error');
                      } finally {
                        setIsSendingReminders(false);
                      }
                    },
                  });
                }}
                disabled={isSendingReminders || isSavingLock}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Bell size={18} />
                {isSendingReminders ? 'Sending...' : 'Send reminder'}
              </button>
              <button
                type="button"
                onClick={handleToggleLock}
                disabled={isSavingLock}
                className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  monthState.locked ? 'bg-red-500/90 hover:bg-red-500' : 'bg-brand-500 hover:bg-brand-400'
                }`}
              >
                {monthState.locked ? <LockOpen size={18} /> : <Lock size={18} />}
                {isSavingLock ? 'Saving...' : monthState.locked ? 'Unlock month' : 'Lock month'}
              </button>
            </div>
          </section>

          {/* Team Hours Table */}
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-brand-200/90">
                <Users size={18} weight="fill" /> Team hours
              </div>
              <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            <div className="mt-4 overflow-hidden rounded-[1.5rem] border border-white/10">
              <div className="grid grid-cols-[1.2fr_0.7fr_1.3fr_0.9fr] bg-black/30 px-4 py-3 text-xs uppercase tracking-[0.25em] text-white/45">
                <div>User</div>
                <div>Hours</div>
                <div>Notes</div>
                <div>Updated</div>
              </div>
              <div className="divide-y divide-white/5">
                {isLoadingEntries ? (
                  <div className="bg-black/10 px-4 py-10 text-center text-sm text-white/45">Loading entries...</div>
                ) : entries.length > 0 ? (
                  entries.map((entry) => (
                    <div key={entry.userId} className="grid grid-cols-[1.2fr_0.7fr_1.3fr_0.9fr] items-center bg-black/10 px-4 py-4 text-sm transition hover:bg-white/[0.04]">
                      <div className="flex items-center gap-3 font-medium text-white">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-400/30 to-brand-600/20 text-xs font-semibold text-brand-100 ring-1 ring-brand-400/20">
                          {(entry.userName || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="truncate">{entry.userName || 'Unknown user'}</span>
                      </div>
                      <div className="font-semibold text-brand-200">{Number(entry.hours || 0).toFixed(2)}</div>
                      <div className="truncate text-white/65">{entry.notes || <span className="text-white/30">No notes</span>}</div>
                      <div className="text-white/45">{new Date(entry.updatedAt).toLocaleString()}</div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center gap-2 bg-black/10 px-4 py-12 text-center text-sm text-white/45">
                    <Users size={28} className="text-white/25" />
                    No entries for this month yet.
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Edit Entries Tab */}
      {activeTab === 'edit' && (
        <section className="rounded-[2rem] border border-white/10 bg-white/10 p-6 text-white shadow-glow backdrop-blur-2xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-brand-200/90">Admin tools</div>
              <h2 className="mt-2 text-2xl font-semibold text-white">Edit or clear a user entry</h2>
              <p className="mt-2 text-sm text-white/65">Pick a user, adjust their hours or notes for the selected month, or clear the entry entirely.</p>
            </div>
            <div className="text-sm text-white/45">Month: {monthLabel(activeMonth)}</div>
          </div>

          {!candidates ? (
            <div className="mt-6 rounded-[1.25rem] border border-white/10 bg-black/20 px-4 py-4 text-sm text-white/45">
              Loading user list...
            </div>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
              <div className="space-y-3">
                <label className="block text-xs uppercase tracking-[0.22em] text-white/45">User</label>
                <select
                  value={selectedEditUserId ?? ''}
                  onChange={(event) => setSelectedEditUserId(event.target.value ? Number(event.target.value) : null)}
                  className="w-full rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                >
                  <option value="" className="bg-slate-900">Select a user</option>
                  {candidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id} className="bg-slate-900">
                      {candidate.name || candidate.primaryEmail}
                    </option>
                  ))}
                </select>

                {selectedEditCandidate ? (
                  <div className="rounded-[1.25rem] border border-white/10 bg-black/20 p-4 text-sm text-white/75">
                    <div className="font-medium text-white">{selectedEditCandidate.name || selectedEditCandidate.primaryEmail}</div>
                    <div className="mt-1 text-xs text-white/45">{selectedEditCandidate.primaryEmail}</div>
                    <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/45">
                      {selectedEditCandidate.hasEntry ? 'Entry exists' : 'No entry yet'}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-5">
                {selectedEditUserId === null ? (
                  <div className="text-sm text-white/45">Select a user to load their current entry.</div>
                ) : isLoadingEditUser ? (
                  <div className="text-sm text-white/45">Loading entry...</div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.22em] text-white/45">Hours</label>
                        <input
                          type="number"
                          value={editingHours}
                          onChange={(event) => setEditingHours(event.target.value)}
                          className="w-full rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex items-end gap-3">
                        <button
                          type="button"
                          onClick={handleSaveEditedEntry}
                          disabled={isSavingEditUser}
                          className="rounded-2xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isSavingEditUser ? 'Saving...' : 'Save entry'}
                        </button>
                        <button
                          type="button"
                          onClick={handleClearEditedEntry}
                          disabled={isSavingEditUser}
                          className="rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Clear entry
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.22em] text-white/45">Notes</label>
                      <textarea
                        value={editingNotes}
                        onChange={(event) => setEditingNotes(event.target.value)}
                        rows={5}
                        className="w-full rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                        placeholder="Optional notes for this month"
                      />
                    </div>

                    <div className="text-sm text-white/65">
                      {selectedEditEntry ? (
                        <div>
                          Current saved value: {Number(selectedEditEntry.hours || 0).toFixed(2)} hours
                          {selectedEditEntry.notes ? `, notes stored` : ', no notes'}
                        </div>
                      ) : (
                        <div>No entry saved for this month yet.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <section className="rounded-[2rem] border border-white/10 bg-white/10 p-6 text-white shadow-glow backdrop-blur-2xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-brand-200/90">Configuration</div>
              <h2 className="mt-2 text-2xl font-semibold text-white">HR Settings</h2>
              <p className="mt-2 text-sm text-white/65">Manage admin users and who needs to fill in hours.</p>
            </div>
            <button
              type="button"
              onClick={loadSettings}
              disabled={isLoadingSettings}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
            >
              <ArrowsClockwise size={16} className={isLoadingSettings ? 'animate-spin' : ''} />
              {isLoadingSettings ? 'Loading...' : 'Reload settings'}
            </button>
          </div>

          <div className="mt-6 space-y-6">
            {/* Admin Users Section */}
            <div className="rounded-[1.25rem] border border-white/10 bg-black/20 p-5">
              <label className="mb-3 block text-sm uppercase tracking-[0.22em] text-white/65">
                Admin Users
              </label>
              <div className="relative">
                <MagnifyingGlass size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="text"
                  value={adminSearchQuery}
                  onChange={(e) => handleAdminSearchChange(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full rounded-2xl border border-white/10 bg-black/30 py-3 pl-11 pr-24 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                />
                {isSearchingAdmins && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-white/45">Searching...</span>}
              </div>
              {/* Search results dropdown */}
              {adminSearchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-auto rounded-xl border border-white/10 bg-black/40">
                  {adminSearchResults.map((user) => (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() => addAdminUser(user)}
                      className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                    >
                      <div className="font-medium">{[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown'}</div>
                      <div className="text-xs text-white/45">{user.primaryEmail}</div>
                    </button>
                  ))}
                </div>
              )}
              {/* Selected admin users */}
              <div className="mt-3 flex flex-wrap gap-2">
                {adminUsers.map((user) => (
                  <span
                    key={user.userId}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-500/20 px-3 py-1.5 text-sm text-white"
                  >
                    <span>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.primaryEmail}</span>
                    <button
                      type="button"
                      onClick={() => removeAdminUser(user.userId)}
                      className="text-white/60 hover:text-white"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {adminUsers.length === 0 && <span className="text-sm text-white/45">No admin users selected</span>}
              </div>
              <div className="mt-2 text-xs text-white/45">Users who can access and modify HR dashboard settings</div>
            </div>

            {/* Filler Users Section */}
            <div className="rounded-[1.25rem] border border-white/10 bg-black/20 p-5">
              <label className="mb-3 block text-sm uppercase tracking-[0.22em] text-white/65">
                Filler Users
              </label>
              <div className="relative">
                <MagnifyingGlass size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="text"
                  value={fillerSearchQuery}
                  onChange={(e) => handleFillerSearchChange(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full rounded-2xl border border-white/10 bg-black/30 py-3 pl-11 pr-24 text-sm text-white outline-none focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                />
                {isSearchingFillers && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-white/45">Searching...</span>}
              </div>
              {/* Search results dropdown */}
              {fillerSearchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-auto rounded-xl border border-white/10 bg-black/40">
                  {fillerSearchResults.map((user) => (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() => addFillerUser(user)}
                      className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                    >
                      <div className="font-medium">{[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown'}</div>
                      <div className="text-xs text-white/45">{user.primaryEmail}</div>
                    </button>
                  ))}
                </div>
              )}
              {/* Selected filler users - Table Style */}
              {fillerUsers.length > 0 ? (
                <div className="mt-3 overflow-hidden rounded-xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-black/30 text-xs uppercase tracking-[0.15em] text-white/50">
                      <tr>
                        <th className="px-4 py-2 text-left w-1/3">VeraConnect User</th>
                        <th className="px-4 py-2 text-left">Slack User Details</th>
                        <th className="px-4 py-2 text-right w-16">Remove</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {fillerUsers.map((user) => {
                        const slackId = slackMappings[user.userId];
                        const slackInfo = slackId ? slackUserDetails[slackId] : null;
                        return (
                          <tr key={user.userId} className="bg-white/[0.02] hover:bg-white/[0.04]">
                            <td className="px-4 py-3">
                              <div className="font-medium text-white">
                                {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown'}
                              </div>
                              <div className="text-xs text-white/45">{user.primaryEmail}</div>
                            </td>
                            <td className="px-4 py-3">
                              {slackInfo ? (
                                <div className="flex items-start gap-3">
                                  {slackInfo.avatar && (
                                    <img src={slackInfo.avatar} alt="" className="w-10 h-10 rounded-lg bg-white/10 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0">
                                    <div className="font-medium text-white">{slackInfo.displayName}</div>
                                    {slackInfo.name && slackInfo.name !== slackInfo.displayName && (
                                      <div className="text-xs text-white/60">@{slackInfo.name}</div>
                                    )}
                                    {slackInfo.email && (
                                      <div className="text-xs text-white/45 truncate">{slackInfo.email}</div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-white/30">
                                  <span className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-lg">?</span>
                                  <span className="italic">No Slack match</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => removeFillerUser(user.userId)}
                                className="text-white/40 hover:text-red-400 transition"
                                title="Remove user"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-white/10 bg-black/20 px-4 py-8 text-center text-sm text-white/45">
                  No filler users selected
                </div>
              )}
              <div className="mt-2 text-xs text-white/45">Users who need to fill in their hours</div>
            </div>

            <div className="flex items-center gap-2 text-xs text-white/45">
              {isSavingSettings ? 'Saving changes...' : 'Changes are saved automatically'}
            </div>
          </div>
        </section>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl border border-white/10 bg-white/10 p-6 shadow-lg backdrop-blur-2xl max-w-sm">
            <h3 className="text-lg font-semibold text-white">{confirmModal.title}</h3>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                className="flex-1 rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmModal.onConfirm()}
                className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slack Matching Modal */}
      {slackModal && slackModal.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-800 p-6 shadow-2xl backdrop-blur-2xl">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">Link to Slack User</h3>
              <p className="mt-1 text-sm text-white/60">
                Match <span className="text-white font-medium">
                  {[slackModal.user.firstName, slackModal.user.lastName].filter(Boolean).join(' ') || slackModal.user.primaryEmail}
                </span> to their Slack account for reminders.
              </p>
            </div>

            {/* Suggested Matches */}
            {slackModal.suggestedMatches.length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2">Suggested matches</div>
                <div className="space-y-2 max-h-40 overflow-auto">
                  {slackModal.suggestedMatches.map((match) => (
                    <button
                      key={match.id}
                      type="button"
                      onClick={() => setSlackModal({ ...slackModal, selectedSlackId: match.id })}
                      className={`w-full flex items-center gap-3 rounded-xl p-3 text-left transition ${
                        slackModal.selectedSlackId === match.id
                          ? 'bg-brand-500/20 border border-brand-400/40'
                          : 'bg-white/5 hover:bg-white/10 border border-white/5'
                      }`}
                    >
                      {match.avatar && (
                        <img src={match.avatar} alt="" className="w-10 h-10 rounded-full bg-white/10" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white text-sm truncate">{match.displayName}</div>
                        {match.email && (
                          <div className="text-xs text-white/50 truncate">{match.email}</div>
                        )}
                      </div>
                      {slackModal.selectedSlackId === match.id && (
                        <span className="text-brand-400 text-xs font-medium">Selected</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Search */}
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2">Or search manually</div>
              <div className="relative">
                <MagnifyingGlass size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="text"
                  value={slackModal.searchQuery}
                  onChange={(e) => handleSlackSearchChange(e.target.value)}
                  placeholder="Search Slack users..."
                  className="w-full rounded-xl border border-white/10 bg-black/30 py-2.5 pl-10 pr-4 text-sm text-white outline-none focus:border-brand-400/60"
                />
                {slackModal.isSearching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/45">...</span>
                )}
              </div>

              {/* Manual Search Results */}
              {slackModal.searchResults.length > 0 && (
                <div className="mt-2 space-y-1 max-h-32 overflow-auto rounded-xl border border-white/10 bg-black/40 p-1">
                  {slackModal.searchResults.map((match) => (
                    <button
                      key={match.id}
                      type="button"
                      onClick={() => setSlackModal({ ...slackModal, selectedSlackId: match.id })}
                      className={`w-full flex items-center gap-2 rounded-lg p-2 text-left transition ${
                        slackModal.selectedSlackId === match.id
                          ? 'bg-brand-500/20'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      {match.avatar && (
                        <img src={match.avatar} alt="" className="w-8 h-8 rounded-full bg-white/10" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{match.displayName}</div>
                        {match.email && (
                          <div className="text-xs text-white/50 truncate">{match.email}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={skipSlackMatch}
                className="flex-1 rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/5 hover:text-white transition"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={confirmSlackMatch}
                disabled={!slackModal.selectedSlackId}
                className="flex-1 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {slackModal.selectedSlackId ? 'Confirm match' : 'Select a user'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;